import React, { useState, useRef } from "react";
import RenderAccordions from "components/product/details/accordions";
import PaymentIcons from "components/payment-icons";
import ReviewsSnippet from "components/reviews-snippet";
import { useGlobalContext } from "context/global";
import CollapsibleInfo from "components/collapsible-info";
import BundleOption, { FrequencyOption, OneTimePurchaseOption } from "./option";
import BundleCuratorGallery from "./gallery";
import useCartSummary from "hooks/useCartSummary";
import Money from "components/money";
import ProductSlider from "components/product-slider";
import RichText from "components/rich-text";
import { formatTitle } from "helpers/format-title";
import clsx from "clsx/lite";
import BundleChoice from "./choice";
import SummaryBox from "components/summary-box";
import { areFrequenciesEqual } from "utils/frequency";

// This function is used to find the bundle that has meals per week and frequency equals to passed arguments
const findBundleByMealsAndFrequency = (bundles, meals, frequency) =>
	bundles.find(bundle => bundle.mealsPerWeek === meals && areFrequenciesEqual(bundle.frequency, frequency));

// One time purchase will always be 1 month box
const oneTimePurchaseFrequency = { intervalCount: 4, interval: "WEEK" };

const BundleCurator = ({ data }) => {
	const {
		mealsPerWeeks, discountInfo, deliveryFrequencies, subscriptionBenefits, oneTimeBenefits,
		allowOneTimePurchase, title, bundles = [], productSlider, hideMealOptions, hidePrice, hideBundle,
		hideCTALabel
	} = data;
	const { translation, paymentIcons, cartSettings } = useGlobalContext();
	const defaultBundle = bundles.find(b => b.isDefault) || bundles[0];
	const [selectedBundle, setSelectedBundle] = useState(defaultBundle);
	const [isOneTimePurchase, setIsOneTimePurchase] = useState(false);
	const frequenciesRef = useRef();

	const updateSelectedBundle = (meals, freq) => {
		const bundle = findBundleByMealsAndFrequency(bundles, meals, freq);
		setSelectedBundle(bundle);
	};

	const bundleDescription = selectedBundle?.description || selectedBundle.product.details.description;

	const currentBox = cartSettings.boxes.find(
		box => box.size.value === selectedBundle.mealsPerWeek * selectedBundle.frequency.intervalCount
	);
	const cartSummary = useCartSummary({
		currentBox, products: [{ ...selectedBundle.product, quantity: 1 }],
		totalWeight: selectedBundle.product.variant.weight,
		calcFirstOrderDiscount: !isOneTimePurchase
	});

	const onMealsPerWeekChange = meals => {
		updateSelectedBundle(meals, selectedBundle.frequency);
		const top = frequenciesRef.current.getBoundingClientRect().top + window.scrollY - 180;
		window.scrollTo({ top, behavior: "smooth" });
	};

	const onFrequencyChange = freq => {
		updateSelectedBundle(selectedBundle.mealsPerWeek, freq);
		if (isOneTimePurchase) setIsOneTimePurchase(false);
	};

	const handleOneTimePurchase = () => {
		setIsOneTimePurchase(true);
		updateSelectedBundle(selectedBundle.mealsPerWeek, oneTimePurchaseFrequency);
	};

	const oneTimePurchase = hideMealOptions && allowOneTimePurchase;

	return (
		<div className="hd-screen-max-width">
			<div className="w-90 m-auto flex flex-col lg:flex-row justify-between gap-x-10 gap-y-5">
				<div className="lg:w-[47%] lg:sticky lg:top-20 h-full">
					<BundleCuratorGallery images={selectedBundle.product?.images} />
					<div className="pt-10 hidden lg:block">
						<RenderAccordions accordions={selectedBundle.accordions} />
					</div>
				</div>
				<div className="flex flex-col md:mt-2 lg:w-[47%] w-full">
					<h2>{formatTitle(title)}</h2>
					{!hidePrice && <div className="flex gap-10 items-center my-3">
						<div className="flex gap-x-2 flex-row">
							<span className="font-boldParagraph text-sm"><Money value={cartSummary.totalPrice} /></span>
							{cartSummary.totalBowlsPrice > 0 && cartSummary.totalBowlsPrice !== cartSummary.totalPrice &&
								<span className="line-through !text-sm" aria-label={`Original price: ${cartSummary.totalBowlsPrice}`}>
									<Money value={cartSummary.totalBowlsPrice} />
								</span>}
						</div>
						<span className="bg-green-discount px-4 py-1.5 text-small rounded-6xl">
							<Money value={cartSummary.pricePerMeal} minimumFractionDigits={0} />/{translation.cart?.meal}
						</span>
					</div>}
					<ReviewsSnippet leftAlign />
					<div className="mb-3">
						<RichText value={bundleDescription} />
					</div>
					<div>
						{!hideMealOptions && <>
							<p className="text-h5 md:text-h4 mt-4 mb-6 font-boldParagraph">
								{translation.shopping_flow?.meals_per_week_title}
							</p>
							<div className="flex flex-row gap-x-3">
								{mealsPerWeeks.map(meals => (
									<BundleOption data={meals} key={meals._key}
										isActive={selectedBundle.mealsPerWeek === meals.value}
										onClick={() => onMealsPerWeekChange(meals.value)}
									/>
								))}
							</div>
						</>}
						{!oneTimePurchase &&
							<p className={clsx("text-h5 md:text-h4 mb-6 font-boldParagraph", hideMealOptions ? "mt-4" : "mt-9")}>
								{translation.shopping_flow?.delivery_frequency_title}
							</p>}
						<div className="flex flex-col gap-y-4.5" ref={frequenciesRef}>
							{deliveryFrequencies.map(frequency => (
								<FrequencyOption
									key={frequency._key}
									title={frequency.name}
									allowOneTimePurchase={allowOneTimePurchase}
									bundle={findBundleByMealsAndFrequency(bundles, selectedBundle.mealsPerWeek, frequency)}
									benefits={allowOneTimePurchase ? subscriptionBenefits : []}
									isActive={!isOneTimePurchase && areFrequenciesEqual(frequency, selectedBundle.frequency)}
									handleClick={() => onFrequencyChange(frequency)} />
							))}
							{allowOneTimePurchase &&
								<OneTimePurchaseOption
									highestBoxPrice={cartSummary.highestBoxPrice}
									benefits={oneTimeBenefits}
									bundle={findBundleByMealsAndFrequency(bundles, selectedBundle.mealsPerWeek, oneTimePurchaseFrequency)}
									isActive={isOneTimePurchase}
									handleClick={handleOneTimePurchase}
								/>
							}
						</div>
					</div>
					{discountInfo && <CollapsibleInfo className="my-6" {...discountInfo} />}
					{!oneTimePurchase &&
						<BundleChoice bundle={selectedBundle} cartSummary={cartSummary}
							frequency={cartSettings.subscription.frequencies.find(f => areFrequenciesEqual(f, selectedBundle.frequency))}
							hideBundle={hideBundle} />
					}
					<SummaryBox
						className={clsx(hideMealOptions && "pt-5")}
						title={title} bundle={selectedBundle}
						cartSummary={cartSummary}
						isOneTimePurchase={isOneTimePurchase}
						hideCTALabel={hideCTALabel}
					/>
					<div className="flex flex-col items-center justify-center">
						<p className="text-gray-500 text-sm">
							{translation.other?.checkoutMessage} | {translation.other?.freeShipping}
						</p>
						{paymentIcons?.length && <div><PaymentIcons data={paymentIcons} settings /></div>}
					</div>
					<div className="pt-2.5 lg:hidden">
						<RenderAccordions accordions={selectedBundle.accordions} />
					</div>
				</div>
			</div>
			{productSlider &&
				<div className="my-5 md:my-10" id="bundle-products">
					<ProductSlider
						navClass="!top-2/3 hover:!top-[67%]"
						data={{ ...productSlider, products: selectedBundle.product.details.products }} />
				</div>}
		</div>
	);
};

export default BundleCurator;
